import { parseTimestamp, getTimestamp, getConsumptionValue } from './dataAlignmentHelpers';

export interface BillValidationResult {
  hasData: boolean;
  billPeriod: string;
  billKwh: number;
  calculatedKwh: number;
  match: boolean;
  difference: number;
  percentDiff: number;
  recordsFound: number;
  billDemandKw: number;
  calculatedDemandKw: number;
  demandMatch: boolean;
  demandDifference: number;
  demandPercentDiff: number;
}

/**
 * Validate consumption data against bill data
 */
export function validateBillConsumption(
  consumption: any,
  bill: any
): BillValidationResult | null {
  if (!consumption || !consumption.data || consumption.data.length === 0) {
    return null;
  }

  if (!bill) {
    return null;
  }

  // Parse bill period - handle both 'period' string and separate period_start/period_end fields
  let billPeriodStart: Date | null = null;
  let billPeriodEnd: Date | null = null;
  let billPeriodString: string = '';

  if (bill.period) {
    // Format: "2024-01-01 to 2024-12-31"
    billPeriodString = bill.period;
    const periodParts = bill.period.split(' to ');
    if (periodParts.length === 2) {
      billPeriodStart = new Date(periodParts[0]);
      billPeriodEnd = new Date(periodParts[1]);
      // Set end date to end of day
      billPeriodEnd.setHours(23, 59, 59, 999);
    }
  } else if (bill.period_start && bill.period_end) {
    // Alternative format with separate fields
    billPeriodStart = new Date(bill.period_start);
    billPeriodEnd = new Date(bill.period_end);
    billPeriodEnd.setHours(23, 59, 59, 999);
    billPeriodString = `${bill.period_start} to ${bill.period_end}`;
  }

  if (!billPeriodStart || !billPeriodEnd || isNaN(billPeriodStart.getTime()) || isNaN(billPeriodEnd.getTime())) {
    // If dates are invalid, show the actual period from the bill
    return {
      hasData: false,
      billPeriod: billPeriodString || 'Unknown period',
      billKwh: bill.consumptionKwh || bill.total_consumption_kwh || 0,
      calculatedKwh: 0,
      match: false,
      difference: 0,
      percentDiff: 0,
      recordsFound: 0,
      // Demand fields
      billDemandKw: bill.peak_demand_kw || bill.peakDemandKw || 0,
      calculatedDemandKw: 0,
      demandMatch: false,
      demandDifference: 0,
      demandPercentDiff: 0
    };
  }

  // Calculate consumption for the bill period
  const periodData = consumption.data.filter((d: any) => {
    const timestamp = getTimestamp(d);
    const date = parseTimestamp(timestamp);
    if (!date) return false;

    return date >= billPeriodStart && date <= billPeriodEnd;
  });

  const calculatedKwh = periodData.reduce((sum: number, d: any) =>
    sum + getConsumptionValue(d), 0
  );

  const billKwh = bill.consumptionKwh || bill.total_consumption_kwh || 0;
  const difference = Math.abs(billKwh - calculatedKwh);
  const percentDiff = billKwh > 0 ? (difference / billKwh) * 100 : 0;
  const match = percentDiff < 1; // Less than 1% difference

  // Calculate peak demand for the bill period
  const calculatedDemandKw = periodData.reduce((max: number, d: any) => {
    const demandValue = d.demand_kw || d.power_kw || d.power || 0;
    return Math.max(max, demandValue);
  }, 0);

  const billDemandKw = bill.peak_demand_kw || bill.peakDemandKw || 0;
  const demandDifference = Math.abs(billDemandKw - calculatedDemandKw);
  const demandPercentDiff = billDemandKw > 0 ? (demandDifference / billDemandKw) * 100 : 0;
  const demandMatch = demandPercentDiff < 5; // Less than 5% difference for demand (more tolerance)

  return {
    hasData: periodData.length > 0,
    billPeriod: billPeriodString || bill.period || '',
    billKwh,
    calculatedKwh: Math.round(calculatedKwh),
    match,
    difference: Math.round(difference),
    percentDiff,
    recordsFound: periodData.length,
    // Demand validation fields
    billDemandKw,
    calculatedDemandKw: Math.round(calculatedDemandKw * 10) / 10, // Round to 1 decimal
    demandMatch,
    demandDifference: Math.round(demandDifference * 10) / 10,
    demandPercentDiff
  };
}
